import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { AddressQueryProps, Comparable } from '@smartflip/data-models';
import {
  AddressAbbreviationsService,
  PropertyService,
} from '@smartflip/helper-utils';
import { environment } from 'apps/app/src/environments/environment';
import { getAttomComps, getDatafinitiComps } from '@smartflip/datafiniti';

export interface Comps {
  compsUrl: string;
  list: Record<string, string>[];
  error?: string;
}

export const EmptyComps: Comparable[] = [];

@Injectable({
  providedIn: 'root',
})
export class ComparablesService {
  private attomAPIKey = environment.attomApiKey;
  private attomAPIurl = 'https://api.gateway.attomdata.com/property/v2/';
  protected datafinitiAPIKey = environment.datafinitiKey;
  protected datafinitiAPIurl = 'https://api.datafiniti.co/v4/properties/search';
  public comps$: BehaviorSubject<Comparable[] | null> = new BehaviorSubject<
    Comparable[] | null
  >(null);
  public error: boolean = false;
  public errorText: string = '';

  constructor(
    private propertyService: PropertyService,
    private http: HttpClient,
    private addressAbbreviationService: AddressAbbreviationsService,
  ) {}

  public setComps(newComps: Comparable[] | null) {
    this.comps$.next(newComps);
  }

  async getComparablesForProperty(
    address: string,
    zip: string,
    fullSearch: AddressQueryProps,
  ): Promise<Comparable[] | null> {
    const useDatafiniti = true; // TODO: make this dynamic
    let comparablesList;
    let unabbreviatedComparables = null;

    if (useDatafiniti) {
      const search = {
        ...fullSearch,
        address: this.addressAbbreviationService.abbreviateAddress(
          fullSearch.address,
        ),
      };
      comparablesList = await getDatafinitiComps(
        search,
        this.datafinitiAPIKey,
        this.datafinitiAPIurl,
        this.http,
      );
    } else {
      comparablesList = await getAttomComps(
        address,
        zip,
        this.attomAPIKey,
        this.attomAPIurl,
        this.http,
      );
    }

    if (comparablesList) {
      unabbreviatedComparables = comparablesList.map((comparable) => ({
        ...comparable,
        street: this.addressAbbreviationService.unabbreviateAddress(
          comparable.street,
        ),
      }));
      this.propertyService.updateProperty(
        'comps',
        unabbreviatedComparables as Comparable[],
      );
    }

    return unabbreviatedComparables;
  }

  async init(): Promise<void> {
    // get existing comps stored in Firestore
    const { comps } = this.propertyService.currentProperty;
    const unabbreviatedComps = comps?.map((comp) => ({
      ...comp,
      street: this.addressAbbreviationService.unabbreviateAddress(comp.street),
    }));
    // Null is used to show the instructions vs "no comps found"
    this.setComps(unabbreviatedComps || null);
  }

  reset() {
    // clear existing comps
    this.setComps(null);
  }
}
